import { ComponentProps } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useContactPanelShallowStore } from '@frontend/shared';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { Button, PopoverMenu, PopoverMenuItem, usePopoverMenu } from '@frontend/design-system';
import { ContactListItem } from './contact-list-item';

type AssociatedContactsListItemProps = Omit<ComponentProps<typeof ContactListItem>, 'children'> & {
  onSelect?: () => void;
  onSetPrimaryContact?: () => void;
  disableRowInteraction?: boolean;
};

/**
 * `AssociatedContactsListItem` is a component that displays a single contact in the associated contacts list.
 * If the `person` prop is provided, it will be used to render the contact. Otherwise, the component will fetch
 * the contact using the `personId` prop.
 *
 * @param onSelect - Callback function to be called when the contact is selected (e.g., clicked).
 * @param onSetPrimaryContact - Callback function to be called when the user wants to set this contact as the primary contact.
 * @param locationId - The ID of the location associated with the person.
 * @param isPrimary - Whether this contact is currently set as the primary contact.
 * @param personId - The ID of the person to display.
 */
export const AssociatedContactsListItem = ({
  onSetPrimaryContact,
  onSelect,
  disableRowInteraction = false,
  locationId,
  isPrimary,
  personId,
  ...rest
}: AssociatedContactsListItemProps) => {
  const { t } = useTranslation('thread-header');

  const { getMenuProps, getItemProps, getTriggerProps } = usePopoverMenu({
    placement: 'bottom-end',
  });

  const { setPersonId } = useContactPanelShallowStore('setPersonId');

  return (
    <>
      <ContactListItem
        locationId={locationId}
        isPrimary={isPrimary}
        personId={personId}
        disableInteraction={disableRowInteraction}
        {...rest}
        trackingId={`${InboxPrefixes.Thread}-associated-contact-focus-contact`}
        onMouseDown={
          onSelect
            ? () => {
                onSelect();
              }
            : undefined
        }
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
          }}
        >
          <Button
            iconName='user-small'
            variant='secondary'
            onClick={() => {
              setPersonId(personId, true, locationId);
            }}
            onMouseDown={(e) => {
              // Prevent the ContactListItem onMouseDown from being triggered
              e.stopPropagation();
            }}
            label={t('Set')}
            size='small'
            trackingId={`${InboxPrefixes.Thread}-associated-contact-view-profile`}
          />
          {onSetPrimaryContact && (
            <Button
              iconName='more-small'
              variant='secondary'
              {...getTriggerProps()}
              onMouseDown={(e) => {
                // Prevent the ContactListItem onMouseDown from being triggered
                e.stopPropagation();
              }}
              label={t('Open Associated Contact Actions')}
              size='small'
              disabled={isPrimary}
              css={
                isPrimary && {
                  backgroundColor: 'transparent',
                }
              }
              trackingId={`${InboxPrefixes.Thread}-associated-contact-more-menu`}
              data-value={personId}
            />
          )}
        </div>
      </ContactListItem>
      {onSetPrimaryContact && (
        <PopoverMenu {...getMenuProps()}>
          <PopoverMenuItem
            {...getItemProps({
              index: 0,
              onClick: (e) => {
                onSetPrimaryContact();
                e.stopPropagation();
              },
              trackingId: `${InboxPrefixes.Thread}-associated-contact-set-primary`,
            })}
            css={{
              cursor: 'pointer',
            }}
          >
            {t('Make Phone Owner')}
          </PopoverMenuItem>
        </PopoverMenu>
      )}
    </>
  );
};
